import {
  useCallback,
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
  type ReactNode,
} from "react";
import { Link, useSearchParams } from "react-router-dom";
import { api } from "../api/apiClient";
import type { CustomerOrder, Location } from "../api/types";
import { MonthCalendar } from "../components/MonthCalendar";
import { DayOrdersModal } from "../components/orders/DayOrdersModal";
import { OrdersHistory } from "../components/orders/OrdersHistory";
import {
  compareOrdersForDisplay,
  orderDayDate,
  orderDayKey,
  orderNeedsCourier,
  orderSlotLabel,
} from "../components/orders/orderUi";
import { SegmentedControl } from "../components/ui/SegmentedControl";
import { WebOrdersNeedSomeone } from "../components/shopify/WebOrdersNeedSomeone";
import {
  endOfTaskMonth,
  sameTaskDay,
  startOfTaskMonth,
} from "../components/tasks/taskUi";
import { useAuth } from "../state/AuthContext";
import { useT } from "../i18n/LanguageProvider";

export function OrdersCalendarPage() {
  const { user, hasRole } = useAuth();
  const [searchParams, setSearchParams] = useSearchParams();
  const ownerScoped = hasRole("OWNER");
  const [locations, setLocations] = useState<Location[]>([]);
  const [locationsLoading, setLocationsLoading] = useState(ownerScoped);

  const refreshLocations = useCallback(async () => {
    if (!ownerScoped) return;
    setLocationsLoading(true);
    try {
      const list = await api.listLocations();
      setLocations(list.filter((loc) => loc.isActive));
    } catch {
      setLocations([]);
    } finally {
      setLocationsLoading(false);
    }
  }, [ownerScoped]);

  useEffect(() => {
    void refreshLocations();
  }, [refreshLocations]);

  useEffect(() => {
    if (!ownerScoped || locations.length === 0) return;
    const q = searchParams.get("locationId");
    if (q && locations.some((loc) => loc.id === q)) return;
    setSearchParams({ locationId: locations[0].id }, { replace: true });
  }, [ownerScoped, locations, searchParams, setSearchParams]);

  const resolvedLocationId = useMemo(() => {
    if (ownerScoped) {
      const q = searchParams.get("locationId");
      if (q && locations.some((loc) => loc.id === q)) return q;
      return locations[0]?.id ?? "";
    }
    return user?.assignedLocation?.id ?? "";
  }, [ownerScoped, searchParams, locations, user?.assignedLocation?.id]);

  if (!user) return null;

  if (ownerScoped && locationsLoading) {
    return (
      <section style={{ display: "flex", flexDirection: "column", gap: "var(--space-4)" }}>
        <div className="empty-state">Loading shops…</div>
      </section>
    );
  }

  if (ownerScoped && locations.length === 0) {
    return (
      <section style={{ display: "flex", flexDirection: "column", gap: "var(--space-4)" }}>
        <h2 className="page-title">Orders</h2>
        <p className="page-subtitle">Add an active shop before scheduling orders.</p>
      </section>
    );
  }

  if (!resolvedLocationId) {
    return (
      <section style={{ display: "flex", flexDirection: "column", gap: "var(--space-4)" }}>
        <h2 className="page-title">Orders</h2>
        <div className="alert error">
          You are not assigned to a shop. Ask an owner to assign you so you can view
          the order calendar.
        </div>
      </section>
    );
  }

  const shopPicker =
    ownerScoped && locations.length > 0 ? (
      <div className="field" style={{ maxWidth: "22rem" }}>
        <label htmlFor="orders-shop-picker">Shop</label>
        <select
          id="orders-shop-picker"
          value={resolvedLocationId}
          onChange={(e) =>
            setSearchParams({ locationId: e.target.value }, { replace: true })
          }
        >
          {locations.map((loc) => (
            <option key={loc.id} value={loc.id}>
              {loc.name} ({loc.code})
            </option>
          ))}
        </select>
      </div>
    ) : null;

  return <OrdersWorkspace locationId={resolvedLocationId} headerExtra={shopPicker} />;
}

function OrdersWorkspace({
  locationId,
  headerExtra,
}: {
  locationId: string;
  headerExtra: ReactNode;
}) {
  const { hasRole } = useAuth();
  const { t } = useT();
  const [location, setLocation] = useState<Location | null>(null);
  // G-14: one page, two views — the calendar for planning, the history for
  // answering "who prepared what / deliveries over 100 €" questions.
  const [view, setView] = useState<"calendar" | "history">("calendar");
  const [orders, setOrders] = useState<CustomerOrder[]>([]);
  const [month, setMonth] = useState<Date>(() => startOfTaskMonth(new Date()));
  const [activeDay, setActiveDay] = useState<Date | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const todayCellRef = useRef<HTMLDivElement | null>(null);
  const scrollTodayAfterLayout = useRef(false);

  useLayoutEffect(() => {
    if (!scrollTodayAfterLayout.current) return;
    scrollTodayAfterLayout.current = false;
    todayCellRef.current?.scrollIntoView({ behavior: "smooth", block: "center" });
  }, [month]);

  const refresh = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const from = startOfTaskMonth(month);
      const to = endOfTaskMonth(month);
      const [locs, monthOrders] = await Promise.allSettled([
        api.listLocations(),
        api.listCustomerOrders({
          locationId,
          from: orderDayKey(from),
          to: orderDayKey(to),
        }),
      ]);
      if (locs.status === "fulfilled") {
        setLocation(locs.value.find((loc) => loc.id === locationId) ?? null);
      }
      if (monthOrders.status === "fulfilled") setOrders(monthOrders.value);
      const failed = [locs, monthOrders].find(
        (result) => result.status === "rejected",
      );
      if (failed?.status === "rejected") {
        setError(
          failed.reason instanceof Error
            ? failed.reason.message
            : "Could not load some order data.",
        );
      }
    } catch (err: unknown) {
      setError(err instanceof Error ? err.message : "Could not load orders.");
    } finally {
      setLoading(false);
    }
  }, [locationId, month]);

  useEffect(() => {
    void refresh();
  }, [refresh]);

  const monthLabel = useMemo(
    () => month.toLocaleDateString(undefined, { month: "long", year: "numeric" }),
    [month],
  );

  /** Priced, scheduled deliveries in this month that nobody is carrying yet. */
  const needsCourierCount = useMemo(
    () => orders.filter(orderNeedsCourier).length,
    [orders],
  );

  const ordersByDay = useMemo(() => {
    const map = new Map<string, CustomerOrder[]>();
    for (const order of orders) {
      const key = orderDayKey(orderDayDate(order));
      const list = map.get(key) ?? [];
      list.push(order);
      map.set(key, list);
    }
    for (const list of map.values()) {
      list.sort(compareOrdersForDisplay);
    }
    return map;
  }, [orders]);

  const locationTitle = location?.name ?? "—";

  return (
    <section style={{ display: "flex", flexDirection: "column", gap: "var(--space-4)" }}>
      <div className="page-header">
        <div>
          <h2 className="page-title">Orders · {locationTitle}</h2>
          <p className="page-subtitle">
            Scheduled customer orders for {locationTitle} — phone, WhatsApp, Instagram, in-shop and online.
          </p>
          {headerExtra ? <div style={{ marginTop: "var(--space-3)" }}>{headerExtra}</div> : null}
        </div>
        <div style={{ display: "flex", gap: "var(--space-2)", flexWrap: "wrap" }}>
          <Link to="/app" className="button plain">
            ← Back to home
          </Link>
          {/* G-15: ONE create door — the unified Build & send flow (describe
              or compose). The ungated "New order" twin is retired, which also
              kills the florist dead-button (route was OWNER/MANAGER-only). */}
          {hasRole("OWNER", "MANAGER") ? (
            <Link
              to={`/app/orders/build?locationId=${encodeURIComponent(locationId)}`}
              className="button"
            >
              Send order
            </Link>
          ) : null}
        </div>
      </div>

      {error ? <div className="alert error">{error}</div> : null}

      {/* G-160 (OD-263): web orders that need someone, above the calendar. */}
      {hasRole("OWNER", "MANAGER") ? <WebOrdersNeedSomeone /> : null}

      <SegmentedControl<"calendar" | "history">
        ariaLabel="Orders view"
        value={view}
        onChange={setView}
        options={[
          { value: "calendar", label: "Calendar" },
          { value: "history", label: "Orders history" },
        ]}
      />

      {view === "history" ? (
        <OrdersHistory locationId={locationId} />
      ) : (
        <div className="card">
          <div className="schedule-toolbar">
            <div className="schedule-month-nav">
              <button
                type="button"
                className="button tinted"
                onClick={() =>
                  setMonth((cur) => new Date(cur.getFullYear(), cur.getMonth() - 1, 1))
                }
              >
                ‹ Prev
              </button>
              <span className="schedule-month-label">{monthLabel}</span>
              <button
                type="button"
                className="button tinted"
                onClick={() =>
                  setMonth((cur) => new Date(cur.getFullYear(), cur.getMonth() + 1, 1))
                }
              >
                Next ›
              </button>
              <button
                type="button"
                className="button plain"
                onClick={() => {
                  scrollTodayAfterLayout.current = true;
                  setMonth(startOfTaskMonth(new Date()));
                }}
              >
                Today
              </button>
            </div>
            {needsCourierCount > 0 ? (
              <Link to="/app/dispatch" className="badge warning">
                {needsCourierCount === 1
                  ? "1 delivery needs a courier"
                  : `${needsCourierCount} deliveries need a courier`}
              </Link>
            ) : null}
            {loading ? <span className="muted">Loading…</span> : null}
          </div>

          <div style={{ marginTop: "var(--space-4)" }}>
            <MonthCalendar
              month={month}
              todayCellRef={todayCellRef}
              renderCell={(cell) => {
                const key = orderDayKey(cell.date);
                const dayOrders = ordersByDay.get(key) ?? [];
                return (
                  <>
                    <div className="month-calendar-day">
                      <span className="month-calendar-mobile-weekday">
                        {cell.date.toLocaleDateString(undefined, { weekday: "short" })}
                      </span>
                      <span className="month-calendar-day-num">{cell.date.getDate()}</span>
                      {cell.isCurrentMonth ? (
                        <button
                          type="button"
                          className="month-calendar-add"
                          onClick={() => setActiveDay(cell.date)}
                          aria-label={`Open orders for ${cell.date.toDateString()}`}
                        >
                          +
                        </button>
                      ) : null}
                    </div>
                    <div className="month-calendar-shifts">
                      {dayOrders.slice(0, 3).map((order) => (
                        <button
                          key={order.id}
                          type="button"
                          className={`order-chip status-${order.status.toLowerCase()}${
                            orderNeedsCourier(order) ? " needs-courier" : ""
                          }`}
                          onClick={() => setActiveDay(cell.date)}
                          title={
                            orderNeedsCourier(order)
                              ? `${orderSlotLabel(t, order)} · ${order.customerName} · needs a courier`
                              : `${orderSlotLabel(t, order)} · ${order.customerName}`
                          }
                        >
                          {orderSlotLabel(t, order)} {order.customerName}
                        </button>
                      ))}
                      {dayOrders.length > 3 ? (
                        <button
                          type="button"
                          className="button plain month-calendar-more"
                          onClick={() => setActiveDay(cell.date)}
                        >
                          +{dayOrders.length - 3} more
                        </button>
                      ) : null}
                    </div>
                  </>
                );
              }}
            />
          </div>
        </div>
      )}

      {activeDay ? (
        <DayOrdersModal
          date={activeDay}
          orders={orders.filter((order) =>
            sameTaskDay(orderDayDate(order), activeDay),
          )}
          onClose={() => setActiveDay(null)}
          onChanged={refresh}
        />
      ) : null}
    </section>
  );
}
